/**
 * The Medium tier's view-lifetime parts (ADR 0006, integration plan slice 2a):
 * one material per surface kind (pixel-art surface, leader cutaway and baked
 * radiance hooks), the lit material of markers and units, the one directional
 * light with its 1024 shadow map and the hemisphere fill (their colours from
 * the theme; the prototype's `dungeon-main.js`), the units' cut-out shadows
 * and the lens pass. The uniforms are refilled per floor (`bindFloor`), so a
 * new floor reuses every compiled program. The shadow map is static (the
 * casters are rock): it is redrawn when a floor is bound or the tier changes.
 */
import type { Theme } from '@ab/content-schema';
import * as THREE from 'three';
import { type CharacterShadows, createCharacterShadows } from './character-shadows.ts';
import { presentation } from './constants.ts';
import { cutawayHook, updateCutaway } from './cutaway.ts';
import { type Lens, createLens } from './lens.ts';
import { type ShaderHook, addShaderHook } from './material-hooks.ts';
import { freeAtlasGpu, holdAtlases } from './pixel-atlas.ts';
import { bindRadiance, createRadianceUniforms, radianceHook } from './radiance-hook.ts';
import { type SurfaceKind, type SurfaceProgram, surfaceProgram } from './surfaces.ts';
import type { UnitPose } from './units.ts';
import type { World } from './world.ts';
import { applyGrade } from './zones.ts';
import { waterFragment, waterFragmentHeader, waterVertex, waterVertexHeader } from './shaders/water.ts';

const kinds: readonly SurfaceKind[] = ['stone', 'rockmass', 'boulder', 'timber', 'iron'];

/** Material kinds a floor's batches take at Medium: the surfaces, the lit markers and units, the unlit glows and the spring water. */
export type MediumKind = SurfaceKind | 'lit' | 'unlit' | 'water';

export type Medium = {
  readonly materials: Readonly<Record<MediumKind, THREE.Material>>;
  /** The baked light with the shader fog, for materials a floor owns (the item billboards). */
  readonly billboardLight: ShaderHook;
  readonly lens: Lens;
  /** Points every uniform at a floor (filling its light volume first) and places the key light over it. */
  bindFloor(world: World, theme: Theme, seed: number): void;
  setActive(active: boolean): void;
  /** Draws one Medium frame: lens focus on the leader, the cutaway, coronas and shafts, the units' shadows. */
  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera, world: World, units: readonly UnitPose[], leader: UnitPose | undefined, visible: (p: UnitPose) => boolean, elevation: (p: UnitPose) => number, time: number): void;
  /** Frees the GPU copies of the targets and atlases (tier Off). */
  release(): void;
  dispose(): void;
};

export const createMedium = (scene: THREE.Scene, halfFloat: boolean): Medium => {
  const k = presentation.medium;
  const radiance = createRadianceUniforms();
  const programs = new Map<SurfaceKind, SurfaceProgram>();
  const materials = {} as Record<MediumKind, THREE.Material>;
  for (const kind of kinds) {
    const program = surfaceProgram(kind);
    programs.set(kind, program);
    const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0, vertexColors: kind === 'rockmass' });
    addShaderHook(material, program.hook);
    addShaderHook(material, cutawayHook);
    addShaderHook(material, radianceHook(radiance, { ramp: true, rock: kind === 'stone' || kind === 'rockmass' || kind === 'boulder' }));
    materials[kind] = material;
  }
  const lit = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 });
  addShaderHook(lit, radianceHook(radiance, { ramp: false, rock: false }));
  materials.lit = lit;
  // Glows keep their authored colour (lantern glass, runes, coals); the water ripples.
  materials.unlit = new THREE.MeshBasicMaterial({ vertexColors: true });
  const waterTime = { value: 0 };
  const water = new THREE.MeshBasicMaterial({ vertexColors: true });
  addShaderHook(water, {
    key: 'spring-water',
    uniforms: { abWaterTime: waterTime },
    vertexHeader: waterVertexHeader,
    fragmentHeader: waterFragmentHeader,
    vertex: [{ chunk: 'begin_vertex', after: waterVertex }],
    fragment: [{ chunk: 'color_fragment', after: waterFragment(presentation.markerDetail.spring.ripple) }],
  });
  materials.water = water;
  const sun = new THREE.DirectionalLight(0xffffff, 1);
  sun.castShadow = true;
  sun.shadow.mapSize.set(k.shadowMapSize, k.shadowMapSize);
  sun.shadow.camera.near = k.sun.near;
  sun.shadow.camera.far = k.sun.far;
  sun.shadow.bias = k.sun.bias;
  sun.shadow.normalBias = k.sun.normalBias;
  const hemi = new THREE.HemisphereLight(0xffffff, 0x000000, 1);
  scene.add(sun, sun.target, hemi);
  const shadows: CharacterShadows = createCharacterShadows(scene);
  const lens = createLens(halfFloat);
  const letGoOfAtlases = holdAtlases();
  const light = new THREE.Vector3();
  const focus = new THREE.Vector3();
  const anchor = new THREE.Vector3();
  const setActive = (active: boolean): void => {
    sun.visible = active;
    hemi.visible = active;
    shadows.mesh.visible = active;
  };
  setActive(false);
  return {
    materials,
    billboardLight: radianceHook(radiance, { ramp: true, rock: false }),
    lens,
    bindFloor(world, theme, seed) {
      world.medium.prepare();
      for (const program of programs.values()) program.update(theme, seed);
      bindRadiance(radiance, world.medium.lighting.bake, world.medium.mask, theme);
      applyGrade(lens.uniforms, theme, world.medium.zoneMap, world.medium.zoneRect);
      const r = theme.lightRules;
      sun.color.set(r.moon);
      sun.intensity = r.moonGainPercent / 100;
      hemi.color.set(r.sky);
      hemi.groundColor.set(r.skyGround);
      hemi.intensity = r.skyGainPercent / 100;
      const { width: w, height: h } = world.floor;
      const size = Math.max(w, h) / 2 + k.sun.margin;
      sun.position.set(w / 2 + k.sun.offset[0], k.sun.offset[1], -h / 2 + k.sun.offset[2]);
      sun.target.position.set(w / 2, 0, -h / 2);
      sun.target.updateMatrixWorld();
      Object.assign(sun.shadow.camera, { left: -size, right: size, top: size, bottom: -size });
      sun.shadow.camera.updateProjectionMatrix();
    },
    setActive,
    render(renderer, scene3d, camera, world, units, leader, visible, elevation, time) {
      const u = lens.uniforms;
      if (leader !== undefined) {
        focus.set(leader.x, k.lens.focusHeight, -leader.y);
        u.focusDistance.value = -focus.clone().applyMatrix4(camera.matrixWorldInverse).z;
        u.focusBand.value.x = Math.min(k.lens.bandMax, Math.max(k.lens.bandMin, focus.project(camera).y * 0.5 + 0.5));
        updateCutaway(camera, anchor.set(leader.x + leader.lungeX, elevation(leader) + k.cutaway.anchor, -(leader.y + leader.lungeY)));
      } else updateCutaway(camera, null);
      u.time.value = time;
      waterTime.value = time;
      world.medium.lighting.update(time, lens.depth, camera);
      shadows.update(units, visible, elevation, light.subVectors(sun.target.position, sun.position).normalize());
      lens.render(renderer, scene3d, camera);
    },
    release() {
      lens.release();
      freeAtlasGpu();
      shadows.release();
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
    },
    dispose() {
      letGoOfAtlases();
      lens.dispose();
      shadows.dispose();
      for (const m of Object.values(materials)) m.dispose();
      sun.dispose();
      hemi.dispose();
      scene.remove(sun, sun.target, hemi);
    },
  };
};
